/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useContext} from "react";
import {useTranslation} from "react-i18next";
import CardComponent from "pages/Front/Booking/components/CardComponent";
import {currencyNumberFormat, translate} from "utils/utility";
import {BookingPaidContext} from "pages/Front/Booking/BookingPaid";
import {ArticleTicketType} from "types/enums/ArticleTicketType";
import {PersonType} from "types/enums/PersonType";

const BookingPaidReceipt = () => {
    const {t} = useTranslation();
    const bookingPaidContext = useContext(BookingPaidContext);

    if (!bookingPaidContext?.hall || !bookingPaidContext?.payment) {
        return null;
    }

    return (
        <div className={"PaymentReceipt flex-grow"}>
            <CardComponent className={"w-full text-sm sm:text-lg"}>
                <div className={"flex flex-col space-y-2 mb-4 pb-4 leading-5"}>
                    <div>{bookingPaidContext.hall.ticket_header}</div>
                    <div>{bookingPaidContext.hall.address}</div>
                    <hr />
                    <h4 className={"font-black"}>{t("Receipt for your purchase")}</h4>
                    <hr />
                    <div className={"flex-grow flex flex-col justify-between"}>
                        <span className={"font-medium mb-2"}>{t("Items bought")}</span>
                        <div className={"flex flex-col space-y-2"}>
                            {bookingPaidContext.payment?.paymentItems &&
                                bookingPaidContext.payment.paymentItems
                                    .sort((a, b) => (a.article_id > b.article_id ? 1 : -1))
                                    .sort((a, b) => (a.price < b.price ? 1 : -1))
                                    .map((paymentItem, index) => {
                                        if (!paymentItem.article) {
                                            return null;
                                        }
                                        return (
                                            <div key={paymentItem.id} className={"odd:bg-gray-50 flex-grow flex flex-row justify-between"}>
                                                <div className={"flex flex-col"}>
                                                    <div className={"space-x-1"}>
                                                        {(paymentItem.article.ticket_type !== ArticleTicketType.Event ||
                                                            paymentItem.quantity > 1 ||
                                                            paymentItem.price > 0) && <span>{paymentItem.quantity}x</span>}
                                                        <span>{translate(paymentItem.article.article_name)}</span>
                                                    </div>
                                                    <small>
                                                        {paymentItem.article?.person_type &&
                                                            paymentItem.article?.person_type != PersonType.Any &&
                                                            t(paymentItem.article?.person_type)}
                                                    </small>
                                                </div>
                                                <span>
                                                    {paymentItem.price ? currencyNumberFormat(paymentItem.price) : currencyNumberFormat(paymentItem.price)}
                                                </span>
                                            </div>
                                        );
                                    })}
                        </div>
                    </div>
                    {bookingPaidContext.payment.paymentItemVouchers && bookingPaidContext.payment.paymentItemVouchers.length > 0 && (
                        <div className={"flex-grow flex flex-col justify-between"}>
                            <span className={"font-medium mb-2"}>{t("Vouchers")}</span>
                            <div className={"flex flex-col"}>
                                {bookingPaidContext.payment?.paymentItemVouchers &&
                                    bookingPaidContext.payment.paymentItemVouchers
                                        .sort((a, b) => (a.id > b.id ? 1 : -1))
                                        .map((paymentItemVoucher, index) => {
                                            if (!paymentItemVoucher.voucher) {
                                                return null;
                                            }
                                            return (
                                                <div key={paymentItemVoucher.id} className={"flex-grow flex flex-row justify-between"}>
                                                    <span>{paymentItemVoucher.voucher.code}</span>
                                                    <span> {paymentItemVoucher.price ? "- " + currencyNumberFormat(paymentItemVoucher.price) : ""}</span>
                                                </div>
                                            );
                                        })}
                            </div>
                        </div>
                    )}

                    {bookingPaidContext.payment.taxes &&
                        bookingPaidContext.payment.taxes.map((tax, index) => {
                            return (
                                <div key={index} className={"flex-grow flex flex-row justify-between font-bold"}>
                                    <span>
                                        {t("MWST")} {Object.keys(tax)[0]}%
                                    </span>
                                    <span>{currencyNumberFormat(Math.floor(Object.values(tax)[0] * 100))}</span>
                                </div>
                            );
                        })}
                    <div className={"flex-grow flex flex-row justify-between font-bold"}>
                        <span>{t("MWST price")}</span>
                        <span>{currencyNumberFormat(bookingPaidContext.payment.amount - bookingPaidContext.payment.nettoPrice)}</span>
                    </div>
                    <div className={"flex-grow flex flex-row justify-between font-bold"}>
                        <span>{t("Netto price")}</span>
                        <span>{currencyNumberFormat(bookingPaidContext.payment.nettoPrice)}</span>
                    </div>
                    <div className={"flex-grow flex flex-row justify-between font-bold"}>
                        <span>{t("Total price")}</span>
                        <span>{currencyNumberFormat(bookingPaidContext.payment.amount)}</span>
                    </div>
                    <hr />
                    <div>{bookingPaidContext.hall.ticket_qrinfo}</div>
                    <div>{bookingPaidContext.hall.ticket_footer}</div>
                </div>
            </CardComponent>
        </div>
    );
};

export default BookingPaidReceipt;
